import styled from '@emotion/styled';
import { faFilter, faMagnifyingGlass, faMap, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SportTypes } from '@racemap/sdk/schema/events';
import type { EventCounts } from '@racemap/utilities/api-client';
import eventTypes, {
  sportGroupBySportType,
  sportGroups,
} from '@racemap/utilities/consts/eventTypes';
import { Badge, Button, Input, Popover, Segmented } from 'antd';
import { type FC, useState } from 'react';
import { GroupedCheckboxPicker } from 'src/components/BasicComponents/GroupedCheckboxPicker/GroupedCheckboxPicker';
import type { EventStatus } from './eventsOverview';

export type StatusFilter = 'all' | EventStatus;

const Bar = styled.section`
  position: sticky;
  top: 0;
  z-index: 500;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 3.5px;
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const SportLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  img {
    width: 18px;
    height: 18px;
  }
`;

type Props = {
  query: string;
  onQueryChange: (q: string) => void;
  status: StatusFilter;
  onStatusChange: (s: StatusFilter) => void;
  sports: Array<SportTypes>;
  onSportsChange: (s: Array<SportTypes>) => void;
  counts: EventCounts | undefined;
  areaActive: boolean;
  onResetArea: () => void;
  hasFilters: boolean;
  onClearAll: () => void;
};

const label = (text: string, n: number | undefined) => (
  <span>
    {text} <span style={{ fontSize: 12, opacity: 0.75 }}>{n ?? ''}</span>
  </span>
);

export const EventsToolbar: FC<Props> = ({
  query,
  onQueryChange,
  status,
  onStatusChange,
  sports,
  onSportsChange,
  counts,
  areaActive,
  onResetArea,
  hasFilters,
  onClearAll,
}) => {
  const total = counts && counts.live + counts.upcoming + counts.past;
  const [sportSearch, setSportSearch] = useState('');
  // Sports without events stay hidden, unless selected.
  const sportItems = eventTypes.flatMap((t) => {
    const group = sportGroupBySportType[t.id];
    const n = counts?.bySport[t.id] ?? 0;
    if (group == null || (n === 0 && !sports.includes(t.id))) return [];
    return [
      {
        key: t.id,
        group,
        searchText: `${t.name} ${t.id}`.toLowerCase(),
        label: (
          <SportLabel>
            <img src={t.symbol} alt="" loading="lazy" />
            {t.name}
            <span style={{ fontSize: 12, color: '#646464' }}>{n}</span>
          </SportLabel>
        ),
      },
    ];
  });

  return (
    <Bar>
      <Input
        size="large"
        allowClear
        style={{ flex: '1 1 260px', minWidth: 0 }}
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} color="#aaaaaa" />}
        placeholder="Search by event, location or country"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
      />
      <Segmented<StatusFilter>
        size="large"
        value={status}
        onChange={onStatusChange}
        options={[
          { value: 'all', label: label('All', total) },
          { value: 'live', label: label('Live', counts?.live) },
          { value: 'upcoming', label: label('Upcoming', counts?.upcoming) },
          { value: 'past', label: label('Past', counts?.past) },
        ]}
      />
      <Popover
        trigger="click"
        placement="bottomRight"
        onOpenChange={(open) => !open && setSportSearch('')}
        content={
          <GroupedCheckboxPicker
            items={sportItems}
            groups={sportGroups}
            selected={new Set(sports)}
            onChange={(next) => onSportsChange([...next])}
            onReset={() => onSportsChange([])}
            searchTerm={sportSearch}
            onSearchTermChange={setSportSearch}
          />
        }
      >
        <Button size="large" icon={<FontAwesomeIcon icon={faFilter} />}>
          Sport <Badge count={sports.length} size="small" color="#36739a" />
        </Button>
      </Popover>
      {areaActive && (
        <Button size="large" onClick={onResetArea} title="Show events everywhere">
          <FontAwesomeIcon icon={faMap} /> Map area <FontAwesomeIcon icon={faXmark} />
        </Button>
      )}
      {hasFilters && (
        <Button type="link" size="large" onClick={onClearAll}>
          Clear filters
        </Button>
      )}
    </Bar>
  );
};
